import React, { forwardRef } from 'react';
import { Card } from '@design-system/card';
import { Carousel, CarouselProps } from '@design-system/carousel';
import { ExtendableImage, ExtendableImageProps } from '@design-system/extendable-image';
import { HeadingBlock } from '@design-system/heading-block';
import { PageBlock } from '@design-system/page-block';
import { RetailerLink, RetailersBlock } from '@design-system/retailers-block';
import TMPLinkWrap from '@husky-x/gatsby-theme-husky/src/components/TMPLinkWrap';
import { headingBlockMapper } from '@husky-x/gatsby-theme-husky/src/mappers/headingBlock';
import { HeadingBlock as HeadingBlockMapperProps } from '@husky-x/gatsby-theme-husky/src/mappers/headingBlock/models';
import { imageMapper } from '@husky-x/gatsby-theme-husky/src/mappers/image';
import { Image as ImageMapperProps } from '@husky-x/gatsby-theme-husky/src/mappers/image/models';
import { linkMapper } from '@husky-x/gatsby-theme-husky/src/mappers/link';
import { Link as LinkMapperProps } from '@husky-x/gatsby-theme-husky/src/mappers/link/models';
import { pageBlockMapper } from '@husky-x/gatsby-theme-husky/src/mappers/pageBlock';
import { PageBlock as PageBlockMapperProps } from '@husky-x/gatsby-theme-husky/src/mappers/pageBlock/models';

import { toCamelCase } from './util';

const PREFERRED_CARD_SIZE = 250;
const CARD_GAP = 24;

type RetailersProps = {
  uid: string;
  pageBlock: PageBlockMapperProps;
  items: {
    retailers: { image: ImageMapperProps; link: LinkMapperProps }[];
    retailersHeading: HeadingBlockMapperProps;
    heading: HeadingBlockMapperProps;
    image: ImageMapperProps;
  }[];
};

export const Retailers = forwardRef<HTMLDivElement, { modelData: unknown }>((props, ref) => {
  const { uid, pageBlock, items } = toCamelCase(props.modelData) as RetailersProps;

  const carouselItems: CarouselProps['items'] = items.map(
    ({ retailers, retailersHeading, heading, image }) => {
      const retailerLinks = retailers.map((item) => {
        const retailerImage = imageMapper(item.image) as ExtendableImageProps;
        const linkItem = { ...item.link, variant: 'retailer', size: 'large' };

        return {
          link: { component: TMPLinkWrap, props: linkMapper(linkItem) },
          children: <ExtendableImage {...retailerImage} />,
        } as RetailerLink;
      });

      return {
        children: (
          <Card hasMedia>
            <Card.CardContent headingBlock={{ ...headingBlockMapper(heading) }}>
              <HeadingBlock {...headingBlockMapper(retailersHeading)} />
              <RetailersBlock retailerLinks={retailerLinks} />
            </Card.CardContent>
            <Card.CardMedia image={imageMapper(image)} />
          </Card>
        ),
      };
    }
  );

  return (
    <PageBlock
      {...pageBlockMapper(pageBlock)}
      id="retailers"
      className="retailers"
      componentUid={uid}
      ref={ref}
    >
      <Carousel
        items={carouselItems}
        variant="fluid"
        preferredCardSize={PREFERRED_CARD_SIZE}
        gap={CARD_GAP}
        progressIndicator="rails"
        centerInsufficientSlides={false}
        buttonPrev={{ 'aria-label': 'Previous retailers' }}
        buttonNext={{ 'aria-label': 'Next retailers' }}
        ariaLivePlaceholder={(activeIndex, totalCount) =>
          `Showing ${activeIndex} of ${totalCount} retailers`
        }
      />
    </PageBlock>
  );
});
